import React from "react";
import {
  Box,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Collapse,
  Typography,
} from "@mui/material";

/**
 * The date window every pawn report filters on, as ONE checkbox plus one or two
 * date fields.
 *
 * This replaced a Single Day / Date Range toggle-button pair sitting next to a
 * separate pair of From/To fields — four controls to express "which days?".
 * Now the first date field is always on screen and "Single Day" (ticked by
 * default) simply decides whether a second one exists: unticking it slides a
 * "To" field open to the right of the first.
 *
 * The two modes keep separate state (`singleDate` vs `fromDate`/`toDate`)
 * because that is what the report screens fetch on, so the toggle syncs them:
 * whichever day is on screen stays on screen when the second field opens or
 * closes, and the window is never inverted the moment it appears.
 *
 * Renders as a fragment of <Grid item>s — drop it straight into a report's
 * filter <Grid container>.
 */

const BLUE = "#2563EB";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";

// Fixed rather than a Grid breakpoint: the "To" field has to animate its width
// from zero, so it can't be a percentage of a column that isn't there yet.
// Narrow enough that BOTH fields still fit one row on a 320px phone once the
// range opens (138 + 16 gutter + 138 = 292), which a percentage column couldn't
// guarantee anyway.
const DATE_W = { xs: 138, sm: 168 };

const ReportDateWindow = ({
  label,
  dateMode,
  setDateMode,
  singleDate,
  setSingleDate,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
}) => {
  const single = dateMode === "single";

  const toggleSingle = (next) => {
    if (next) {
      // Range → single: the "From" day on screen becomes THE day.
      setSingleDate(fromDate);
      setDateMode("single");
    } else {
      // Single → range: the visible field turns into "From" without its value
      // changing, and "To" opens on the same day rather than on a stale one
      // that would read as a backwards window.
      setFromDate(singleDate);
      if (!toDate || toDate < singleDate) setToDate(singleDate);
      setDateMode("range");
    }
  };

  return (
    <>
      <Grid item xs={12} md="auto">
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 700,
            color: INK_MUTED,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={single}
              onChange={(e) => toggleSingle(e.target.checked)}
              sx={{ py: 0.25, color: INK_MUTED, "&.Mui-checked": { color: BLUE } }}
            />
          }
          label="Single Day"
          sx={{
            m: 0,
            "& .MuiFormControlLabel-label": {
              fontSize: "0.78rem",
              fontWeight: 600,
              color: INK_SOFT,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} sm="auto" sx={{ minWidth: 0, maxWidth: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "flex-end", maxWidth: "100%" }}>
          <TextField
            label={single ? "Date" : "From"}
            type="date"
            size="small"
            value={single ? singleDate : fromDate}
            onChange={(e) =>
              single ? setSingleDate(e.target.value) : setFromDate(e.target.value)
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: DATE_W, flexShrink: 0 }}
          />
          {/* Stays mounted so the slide runs both ways; its left padding is
              inside the clipped area, so nothing gaps when it's shut. */}
          <Collapse
            in={!single}
            orientation="horizontal"
            timeout={250}
            sx={{ flexShrink: 0 }}
          >
            <Box sx={{ pl: 2 }}>
              <TextField
                label="To"
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: DATE_W }}
              />
            </Box>
          </Collapse>
        </Box>
      </Grid>
    </>
  );
};

export default ReportDateWindow;
